/**
 * The clinic's legal audit trail (Treatment Plan spec v1.4, LA-007): every
 * signature, consent, recall, notice, side effect report and export, newest
 * first, paged on the server. The hash chain is checked on every load, and
 * the export is the verifiable JSON format from the spec's code pack. The
 * export is itself recorded (EV-51).
 */

import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, EmptyState, ErrorState, InfoHint, LoadingRows, PageHeader, Table, Td, Th, inputClass } from '../components/ui';

interface TrailPage {
  total: number;
  page: number;
  pageSize: number;
  eventTypes: string[];
  verified: boolean;
  breaks: number;
  rows: Array<{
    id: string;
    chainKey: string;
    seq: number;
    eventType: string;
    actorRole: string;
    actorName: string | null;
    occurredAt: string;
    detail: Record<string, unknown>;
    hash: string;
  }>;
}

const words = (code: string) => {
  const text = code.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const chainLabel = (key: string) => {
  const [kind] = key.split(':');
  return kind === 'plan' ? 'Plan' : kind === 'prescriber' ? 'Prescriber' : kind === 'clinic' ? 'Clinic' : kind === 'patient' ? 'Patient' : words(kind ?? key);
};

export function LegalAuditTrailPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const trail = useQuery({
    queryKey: ['legal-audit', page, eventType],
    queryFn: () => api.get<TrailPage>(`/legal-audit?page=${page}&pageSize=25${eventType ? `&eventType=${encodeURIComponent(eventType)}` : ''}`),
    placeholderData: keepPreviousData,
  });
  const d = trail.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;

  const download = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await api.downloadPdf('/legal-audit/export');
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `legal-audit-trail-${new Date().toISOString().slice(0, 10)}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
      void qc.invalidateQueries({ queryKey: ['legal-audit'] });
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Could not export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader title="Legal audit trail" subtitle="Every signature, consent, recall, notice and export for your clinic, newest first." />
      <Card padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Event"
              className={`${inputClass} w-auto`}
              value={eventType}
              onChange={(e) => {
                setEventType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Every event</option>
              {d?.eventTypes.map((t) => <option key={t} value={t}>{words(t)}</option>)}
            </select>
            {d && (
              <span
                className={`inline-flex items-center rounded-[5px] px-2 py-[3px] text-[12px] font-semibold ${d.verified ? 'bg-mint text-forest' : 'bg-alert-soft text-alert'}`}
                data-testid="trail-verified"
              >
                {d.verified ? 'Chain verified' : `${d.breaks} break${d.breaks === 1 ? '' : 's'} found`}
              </span>
            )}
            <InfoHint label="About the audit trail">
              Each entry is hashed with the one before it, so any edit or deletion breaks the chain. MCOS checks every chain whole. The export holds your clinic's entries with their hashes; gaps in a prescriber's entry numbers are entries from MCOS or other clinics.
            </InfoHint>
          </div>
          <Button variant="secondary" loading={exporting} onClick={() => void download()}>Export</Button>
        </div>
        {exportError && <p className="px-4 pt-3 text-sm text-red-600" role="alert">{exportError}</p>}
        {trail.isLoading && <div className="p-4"><LoadingRows rows={6} /></div>}
        {trail.error && <div className="p-4"><ErrorState error={trail.error} retry={() => void trail.refetch()} /></div>}
        {d && d.rows.length === 0 && <EmptyState title="Nothing recorded yet" body="Signatures, consents and other legal events appear here as they happen." />}
        {d && d.rows.length > 0 && (
          <div className="overflow-x-auto" data-testid="audit-trail">
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Event</Th>
                  <Th>Who</Th>
                  <Th>Record</Th>
                  <Th>Hash</Th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.id}>
                    <Td className="whitespace-nowrap">{new Date(r.occurredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</Td>
                    <Td>{words(r.eventType)}</Td>
                    <Td>{r.actorName ?? words(r.actorRole)}</Td>
                    <Td className="whitespace-nowrap">{chainLabel(r.chainKey)} · entry {r.seq}</Td>
                    <Td><code className="text-[12px] text-[#5b6b7c]">{r.hash.slice(0, 12)}</code></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {d && d.total > d.pageSize && (
          <div className="flex items-center justify-between border-t border-hairline px-4 py-3 text-[13.5px]">
            <span className="text-[#5b6b7c]">Page {d.page} of {pages} · {d.total} entries</span>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
